import React, { createContext, useState, useContext, useEffect } from "react";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);

  useEffect(() => {
    const savedCart = localStorage.getItem("gutful_cart");
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("gutful_cart", JSON.stringify(cart));
  }, [cart]);

  const addToCart = (item, selectedAddons = []) => {
    const addonTotal = selectedAddons.reduce(
      (sum, addon) => sum + addon.price,
      0
    );
    const cartItem = {
      ...item,
      cartId: Date.now() + Math.random(),
      quantity: 1,
      addons: selectedAddons,
      totalPrice: item.selectedPortion
        ? (item.selectedPortion.discountPrice || item.selectedPortion.price) + addonTotal
        : (item.discountPrice || item.price) + addonTotal,
      priceForPromo: item?.noDiscount ? 0 : item.selectedPortion
        ? item.selectedPortion.price
        : item.price,
      originalPrice: item.selectedPortion
        ? item.selectedPortion.price
        : item.price,
      hasDiscount: item.selectedPortion
        ? !!item.selectedPortion.discountPrice
        : !!item.discountPrice,
      discountedPrice: item.selectedPortion
        ? item.selectedPortion.discountPrice || item.selectedPortion.price
        : item.discountPrice || item.price,
      totalMenuItemPrice: item.selectedPortion
        ? item.selectedPortion.price + addonTotal
        : item.price + addonTotal,
      totalDiscountPerItem: item.selectedPortion
        ? item.selectedPortion.price - item.selectedPortion.discountPrice
        : item.price - item.discountPrice,
    };
    setCart([...cart, cartItem]);
  };

  const updateQuantity = (cartId, delta) => {
    setCart(
      cart
        .map((item) =>
          item.cartId === cartId
            ? { ...item, quantity: Math.max(0, item.quantity + delta) }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const removeFromCart = (cartId) => {
    setCart(cart.filter((item) => item.cartId !== cartId));
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem("gutful_cart");
  };

  const getCartTotal = () => {
    const subtotal = cart.reduce(
      (sum, item) => sum + item.totalPrice * item.quantity,
      0
    );
    const subTotalForPromo = cart.reduce(
      (sum, item) => sum + (!item.hasDiscount ? item.priceForPromo * item.quantity : 0),
      0
    );

    const originalPriceTotal = cart.reduce(
      (sum, item) => sum + item.originalPrice * item.quantity,
      0
    ) || 0;

    const totalBeforeDiscount = cart.reduce(
      (sum, item) => sum + item.totalMenuItemPrice * item.quantity,
      0
    ) || 0;

    const totalDiscountOnItems = cart.reduce(
      (sum, item) => sum + item.totalDiscountPerItem * item.quantity,
      0
    ) || 0;

    return { subtotal, subTotalForPromo, total: subtotal, totalBeforeDiscount, totalDiscountOnItems, originalPriceTotal };
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        getCartTotal,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
